import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHasIntegrationApi, useHasSubscriptionManagement } from '~/hooks/useFeatureFlag';
import { useCanAccessFeqCredentialSettings } from '~/hooks/useUserRole';
import SettingsIcon from '~/icons/icon-settings.svg?react';
import { RouteConfig } from '~/routes/routeConfig';
import { useCurrentSubscription, useNavigateToCustomerPortal } from '~/services/subscription';
import { useIsSubmenuOpen, useSetSubmenu } from '~/state/navigationSidebar';
import { SubmenuItem, SubmenuSection } from '~/types/sidebar';
import { SubscriptionStatus } from '~/types/subscriptions';
import NavigationSidebarItem from '../NavigationSidebarItem';

interface Props {
  isCompanyAccessible: boolean;
}

const SettingsItem: React.FC<Props> = ({ isCompanyAccessible }) => {
  const { t } = useTranslation('sidebar');
  const navigateToCustomerPortal = useNavigateToCustomerPortal();
  const { data: subscription } = useCurrentSubscription();
  const hasIntegrationApi = useHasIntegrationApi();
  const hasSubscriptionManagement = useHasSubscriptionManagement();
  const canAccessFeqCredentialSettings = useCanAccessFeqCredentialSettings();
  const isPlansSelectionRoute = location.pathname.startsWith('/plans/select');
  const isSubmenuOpen = useIsSubmenuOpen('settings');
  const openSubmenu = useSetSubmenu('settings');

  const showPlan =
    hasSubscriptionManagement &&
    (isPlansSelectionRoute || isCompanyAccessible) &&
    subscription?.status !== SubscriptionStatus.PreTrial;

  const sections: SubmenuSection[] = useMemo(
    () => [
      {
        items: [
          ...(showPlan
            ? [
                {
                  label: t('settings.submenu.subscription'),
                  action: () => {
                    if (isCompanyAccessible) {
                      navigateToCustomerPortal();
                    }
                  },
                } satisfies SubmenuItem,
              ]
            : []),
          { label: t('settings.submenu.users'), href: RouteConfig.SettingsTeam.buildLink() } satisfies SubmenuItem,
          {
            label: t('settings.submenu.security'),
            href: RouteConfig.SettingsSecurity.buildLink(),
          } satisfies SubmenuItem,
          ...(hasIntegrationApi
            ? [
                {
                  label: t('settings.submenu.api_key'),
                  href: RouteConfig.SettingsApiKey.buildLink(),
                } satisfies SubmenuItem,
              ]
            : []),
          ...(canAccessFeqCredentialSettings
            ? [
                {
                  label: t('settings.submenu.feq_credential'),
                  href: RouteConfig.SettingsFeqCredential.buildLink(),
                } satisfies SubmenuItem,
              ]
            : []),
        ],
      },
    ],
    [t, showPlan, isCompanyAccessible, navigateToCustomerPortal, hasIntegrationApi, canAccessFeqCredentialSettings],
  );

  return (
    <NavigationSidebarItem
      iconComponent={SettingsIcon}
      isSubmenuOpen={isSubmenuOpen}
      onOpenSubmenu={openSubmenu}
      title={t('settings.label')}
      isActive={false}
      submenuSections={sections}
    />
  );
};

export default memo(SettingsItem);
